import {
  countryLongNameToEnumMap,
  FINLAND_STANDARD_VALUE_CALCULATION_TYPE_IDS,
} from "@/providers/Localization/localizationConfig";
import { Country_Enum } from "@/providers/Localization/types/LocalizationTypes";
import calculationTypeVariant from "@/services/enums/calculationTypeVariant";

/**
 * Which parts of a merged wizard step apply to a project.
 *
 * These used to be expressed in two places at once: a `projectCondition` in `createWizardSteps.ts` decided
 * whether the step existed, and the per-country arrays in `localizationConfig.ts` decided whether it was
 * even a candidate. Folding Reference area into Building, and Standard values together with Special
 * conditions, means neither is a whole step any more — so the rule has to move here, where the merged
 * step's `valid()` and the half's own `v-if` can read the SAME answer.
 *
 * That is the point of the file rather than a tidiness exercise. A `v-if` and a `valid()` that disagree
 * about whether a half is on screen produce a step that either blocks on a field nobody can see or waves
 * through one that is invalid, and this branch has already shipped that bug once: the clone address gate
 * showed a red error and advanced anyway because two copies of "is the address optional" disagreed.
 *
 * Pure, and deliberately importing only leaf modules. `@/providers/Localization` is a barrel that pulls
 * `LocalizationProvider`, which pulls `createWizardSteps`, which pulls every WizardFlow component and with
 * them the Apollo client and the 3D viewer — none of which construct under vitest's node environment. The
 * calculation type is passed IN rather than looked up for the same reason: `useCalculationTypes` would drag
 * the same weight in.
 */

/** What these rules need from a calculation type. Structural, so both the list row and a single record fit. */
export type ConditionCalculationType = {
  id: number;
  country?: string | null;
  variant?: { variant?: string | null } | null;
  specialConditionTypes_aggregate?: { aggregate?: { count?: number | null } | null } | null;
};

/** What they need from a project. */
export type ConditionProject = {
  buildingAddress: { country?: string | null };
  emissionCalculationAssumptions: {
    calculationType: { calculationTypeEnum?: number | null };
  };
};

const countryOf = (project: Pick<ConditionProject, "buildingAddress"> | null | undefined): Country_Enum | undefined =>
  countryLongNameToEnumMap[project?.buildingAddress?.country ?? ""];

/**
 * Countries whose create flow asked for reference areas as a step of its own: Sweden's flow, plus the
 * Rigsfaellesskabet one shared by Denmark, the Faroe Islands and Greenland.
 *
 * A list here rather than membership of a flow array, because Building is in every country's flow and the
 * reference-area half now rides along inside it. This is the country knowledge that used to be implicit in
 * `createWizardSwedenFlow` and `createWizardRigsfaellesskabetFlow` naming the step.
 */
const REFERENCE_AREA_COUNTRIES: readonly Country_Enum[] = [
  Country_Enum.SWEDEN,
  Country_Enum.DENMARK,
  Country_Enum.FAROE_ISLAND,
  Country_Enum.GREENLAND,
];

/** Greenland's Level(s) calculation type, which is measured against no reference area. */
const GREENLAND_LEVELS_CALCULATION_TYPE_ID = 31;

export const referenceAreaApplies = (project: ConditionProject): boolean => {
  const country = countryOf(project);
  if (!country || !REFERENCE_AREA_COUNTRIES.includes(country)) return false;

  // Carried over verbatim from the REFERENCE_AREA step's own projectCondition.
  if (
    country === Country_Enum.GREENLAND &&
    project?.emissionCalculationAssumptions?.calculationType?.calculationTypeEnum === GREENLAND_LEVELS_CALCULATION_TYPE_ID
  ) {
    return false;
  }

  return true;
};

/**
 * Countries whose create flow offered standard values: Iceland's and Finland's flows, plus the
 * Rigsfaellesskabet one.
 */
const STANDARD_VALUES_COUNTRIES: readonly Country_Enum[] = [
  Country_Enum.ICELAND,
  Country_Enum.FINLAND,
  Country_Enum.DENMARK,
  Country_Enum.FAROE_ISLAND,
  Country_Enum.GREENLAND,
];

/**
 * Whether standard values apply, which turns on the calculation type rather than the country alone.
 *
 * Denmark exposes them only for the BR18 (2025) variant; Finland only for its two "finish" types, which
 * carry no variant and so are matched by id. Everywhere else on the list they always apply — which is how
 * Iceland, the Faroe Islands and Greenland behave today, since their address country is not the string
 * "Denmark" that the original condition tested.
 */
export const standardValuesApplies = (
  project: ConditionProject,
  calculationType: ConditionCalculationType | undefined
): boolean => {
  const country = countryOf(project);
  if (!country || !STANDARD_VALUES_COUNTRIES.includes(country)) return false;

  if (calculationType) {
    if (calculationType.country === Country_Enum.DENMARK) {
      return calculationType.variant?.variant === calculationTypeVariant.BR182025;
    }
    if (calculationType.country === Country_Enum.FINLAND) {
      return FINLAND_STANDARD_VALUE_CALCULATION_TYPE_IDS.includes(calculationType.id);
    }
  }

  // Nothing chosen yet. Denmark and Finland decide on the type, so they show nothing until there is one.
  if (country === Country_Enum.DENMARK || country === Country_Enum.FINLAND) return false;

  return true;
};

/**
 * Whether the project's calculation type has special conditions to configure.
 *
 * Only the Rigsfaellesskabet flow offered this step, but the country list is not needed: no other
 * country's calculation types carry `specialConditionTypes`, so the aggregate answers it on its own. Left
 * that way deliberately rather than adding a redundant list that could drift from the data.
 */
export const specialConditionsApply = (
  project: ConditionProject,
  calculationType: ConditionCalculationType | undefined
): boolean => {
  if (!project?.emissionCalculationAssumptions?.calculationType?.calculationTypeEnum && !calculationType) return false;

  return !!calculationType?.specialConditionTypes_aggregate?.aggregate?.count;
};

/** Whether the merged Allowances step has anything to show at all. */
export const allowancesApply = (project: ConditionProject, calculationType: ConditionCalculationType | undefined): boolean =>
  standardValuesApplies(project, calculationType) || specialConditionsApply(project, calculationType);

/** Countries whose Transport (A4) settings section is hidden: the data source is picked in the mapping view instead. */
const TRANSPORT_SOURCE_IN_MAPPING_COUNTRIES: readonly Country_Enum[] = [Country_Enum.NORWAY, Country_Enum.FINLAND];

/** True for countries that choose the transport data source per material; the Transport page stays. Unknown country is false. */
export const picksTransportSourceInMapping = (project: Pick<ConditionProject, "buildingAddress"> | null | undefined): boolean => {
  const country = countryOf(project);
  return !!country && TRANSPORT_SOURCE_IN_MAPPING_COUNTRIES.includes(country);
};
